import React, { Component } from "react";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import { Helmet } from "react-helmet";

import Dashboard from "dashboard/containers/Dashboard";
import Settings from "settings/containers/Settings";
import EditorContainer from "editor/containers/EditorContainer";
import DeepDiveEditorContainer from "deepDiveEditor/containers/DeepDiveEditorContainer";
import DeepDiveStatusChange from "deepDiveEditor/containers/DeepDiveStatusChange";
import Preview from "editor/containers/Preview";
import Revert from "editor/containers/Revert";
import StatusChange from "editor/components/StatusChange";
import AlertEditor from "editor/components/AlertEditor";
import PrimaryImageEditor from "editor/containers/PrimaryImageEditor";
import SocialImageEditor from "editor/containers/SocialImageEditor";
import ChunkImageEditor from "editor/containers/ChunkImageEditor";
import BlockImageEditor from "editor/containers/BlockImageEditor";
import PrivateRoute from "auth/components/PrivateRoute";
import Login from "auth/components/Login";
import Logout from "auth/components/Logout";
import AuthRedirect from "auth/components/AuthRedirect";
import ErrorBoundary from "components/library/ErrorBoundary";
import ErrorPage from "components/library/ErrorPage";
import { IMAGE_ROUTES, UUID_REGEXP } from "config/constants";
import IlloLibrary from "illos/containers/IlloLibrary";
import Curation from "curation/containers/Curation";

import "normalize.css";
import styles from "./App.css";

moment.tz.setDefault("America/New_York");

const UUID = UUID_REGEXP.source;

class App extends Component {
  render() {
    const { error } = this.props;

    if (error) {
      return <ErrorPage error={error} />;
    }

    return (
      <ErrorBoundary>
        <Helmet defaultTitle="Eden" titleTemplate="%s - Eden" />

        <Router>
          <div className={styles.root}>
            <Switch>
              {/* Dashboard routes */}
              <PrivateRoute
                exact
                path="/"
                render={() => <Redirect to="/dashboard" />}
              />
              <PrivateRoute path="/dashboard" component={Dashboard} />

              {/* Previewer routes */}
              <PrivateRoute
                path={`/editor/:type/:id${UUID}/preview/:revision?`}
                component={Preview}
              />

              {/* Image editor routes */}
              <PrivateRoute
                path={`/editor/:type(newsletter|story)/:id${UUID}/:image(${IMAGE_ROUTES.PRIMARY})`}
                component={PrimaryImageEditor}
              />
              <PrivateRoute
                path={`/editor/:type(newsletter|story)/:id${UUID}/chunk/:chunkId${UUID}/:image(${IMAGE_ROUTES.PRIMARY})`}
                component={ChunkImageEditor}
              />
              <PrivateRoute
                path={`/editor/:type(newsletter|story)/:id${UUID}/:image(${IMAGE_ROUTES.SOCIAL})`}
                component={SocialImageEditor}
              />
              <PrivateRoute
                path={`/editor/:type(newsletter|story)/:id${UUID}?/:image(${IMAGE_ROUTES.BLOCK})`}
                component={BlockImageEditor}
              />

              {/* Status change routes */}
              <PrivateRoute
                path={`/editor/:type(newsletter|story)/:id${UUID}/:status(schedule|unschedule|unpublish|unfeature|delete|update)`}
                component={StatusChange}
              />
              <PrivateRoute
                path={`/editor/:type(story)/:id${UUID}/:status(publish|breaking)`}
                component={StatusChange}
              />
              <PrivateRoute
                path={`/editor/:type(newsletter)/:id${UUID}?/:status(send)`}
                component={StatusChange}
              />

              {/* Routes for reverting to revision */}
              <PrivateRoute
                exact
                path={`/editor/:type/:id${UUID}/revert/:revision`}
                component={Revert}
              />

              {/* Story editor routes */}
              <PrivateRoute
                path={`/editor/:type(story)/:id${UUID}/send-alert`}
                component={AlertEditor}
              />
              <PrivateRoute
                path={`/editor/:type(story)/:id${UUID}?/:action(content|metadata|social-seo|publishing|previous-versions|schedule)?`}
                component={EditorContainer}
              />
              <PrivateRoute
                path={`/editor/:type(deep-dive)/:id${UUID}/:action(update|publish|unpublish|delete)`}
                component={DeepDiveStatusChange}
              />
              <PrivateRoute
                path={`/editor/:type(deep-dive)/:id${UUID}?/:action(content|metadata)?`}
                component={DeepDiveEditorContainer}
              />

              {/* Newletter editor routes */}
              <PrivateRoute
                path={`/editor/:type(newsletter)/:id${UUID}?`}
                render={props => <EditorContainer {...props} />}
              />

              {/* Redirect plain old editor */}
              <PrivateRoute
                exact
                path="/editor"
                render={() => <Redirect to="/dashboard" />}
              />

              {/* Routes for authentication/authorization */}
              <Route exact path="/login" component={Login} />
              <Route exact path="/logout" component={Logout} />
              <Route exact path="/authorize" component={AuthRedirect} />

              {/* Settings routes */}
              <PrivateRoute
                exact
                path="/settings"
                render={() => <Redirect to="/settings/trending" />}
              />

              <PrivateRoute path="/settings" component={Settings} />

              <PrivateRoute exact path="/illos" component={IlloLibrary} />

              <PrivateRoute path="/curation" component={Curation} />

              {/* 404 Route */}
              <PrivateRoute
                render={() => {
                  const err = new Error();
                  err.status = 404;
                  return <ErrorPage error={err} />;
                }}
              />
            </Switch>
          </div>
        </Router>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => {
  return {
    error: state.error,
  };
};

export default connect(
  mapStateToProps,
  {}
)(App);
